import { request } from './client';
import type { BeginPaymentRequest, BeginPaymentResponse, PaymentResult } from './types';

/**
 * Taking the money.
 *
 * Three calls, and none of them says what anything costs. The tip is the only figure that leaves this app:
 * every other one is read from the POS inside the server's own BeginPayment, because a client that could
 * state what it owes could state less.
 *
 * As with the check, there is no check id in any path — it lives in the session token's signature, and a
 * guest cannot edit their way onto someone else's payment. The payment id IS in the path, and is safe for the
 * same reason it is useful: the server minted it, and a payment is scoped to the session's location.
 */

/** Starts a payment and returns the first thing the client must do. */
export function beginPayment(
    token: string,
    body: BeginPaymentRequest,
    signal?: AbortSignal,
): Promise<BeginPaymentResponse> {
    return request<BeginPaymentResponse>('/payments', { method: 'POST', body, token, signal });
}

/**
 * Hands back whatever the gateway produced.
 *
 * `result` is forwarded whole and unread — the token from a postMessage, or a payload a hosted page returned.
 * The client does not know a signature from a reference and must never be tempted to decide a payment
 * succeeded on the strength of one.
 */
export function completePayment(
    token: string,
    paymentId: string,
    result: Record<string, unknown>,
    signal?: AbortSignal,
): Promise<PaymentResult> {
    return request<PaymentResult>(`/payments/${encodeURIComponent(paymentId)}/complete`, {
        method: 'POST',
        body: { result },
        token,
        signal,
    });
}

/**
 * Where a payment has got to.
 *
 * How the client recovers after a hosted page sends the browser back: the page that started the payment is
 * gone, and this is the only thing that knows what happened while it was away. Deliberately used instead of
 * reading the gateway's response out of the query string, which would put the client back in the business of
 * interpreting a payload it has no business interpreting.
 */
export function getPayment(token: string, paymentId: string, signal?: AbortSignal): Promise<PaymentResult> {
    return request<PaymentResult>(`/payments/${encodeURIComponent(paymentId)}`, { token, signal });
}

/**
 * Exchanges Apple's validation URL for a merchant session.
 *
 * Apple hands the browser a URL partway through the sheet and expects a signed session back. The browser
 * cannot produce one: answering requires the merchant certificate, which belongs on the server and nowhere
 * near a client. So the URL is passed across and whatever comes back is handed to Apple unread.
 *
 * Apple Pay does not work at all without this, which is easy to miss when scoping the wallet.
 *
 * Two things differ from what was drafted for this:
 *
 *   - No payment id. Nothing about merchant validation varies per payment, so there was nothing for one to
 *     select; the location comes off the session token like everywhere else in this API.
 *   - No envelope. The response body is Apple's session and nothing else. Wrapping it would have meant the
 *     server parsing and re-serialising a signed payload for the sake of a one-field object.
 */
export function validateWalletMerchant(
    token: string,
    validationUrl: string,
    signal?: AbortSignal,
): Promise<Record<string, unknown>> {
    return request<Record<string, unknown>>('/payments/wallet-session', {
        method: 'POST',
        body: { validationUrl },
        token,
        signal,
    });
}
